/**
 * useRepoOAuthConnection Hook
 *
 * React hook for managing OAuth authorization flows when connecting
 * repository providers (Azure DevOps, GitHub) during sandbox creation.
 *
 * Follows the same consent pattern as useOAuthConnection but scoped
 * to repository OAuth connectors (azurelogicappsado / ottogithub).
 *
 * Flow:
 * 1. Create connection resource in Azure (no auth)
 * 2. Get OAuth consent link from APIHub
 * 3. Open consent popup — user authenticates with ADO/GitHub
 * 4. Wait for callback (postMessage + URL polling)
 * 5. Confirm consent code
 * 6. Test connection
 * 7. Return connectionId for sandbox repository settings
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/auth/useAuth';
import { isArmMockMode } from '@/services/armConfig';
import { buildOAuthRedirectUrl, extractOAuthErrorCode, getOAuthErrorMessage } from '@/services/oauthService';
import { OAUTH_CHANNEL_NAME } from '@/services/oauthChannel';
import {
  createRepoConnection,
  getRepoConsentLink,
  confirmRepoConsent,
  testRepoConnection,
  generateRepoConnectionName,
  getRepoConnector,
  type RepoConnectorConfig,
} from '@/services/repoConnectionService';
import { type ConnectionResource, type TestConnectionResult } from '@/services/connectionApi';
import type { RepoConnectorType } from '@/types/sandbox';
import { telemetryService } from '@/services/telemetryService';
import { useAppStore } from '@/stores/appStore';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { AppContext } from '@/services/appClient';


// =============================================================================
// TYPES
// =============================================================================

/**
 * Parameters for initiating a repo OAuth connection.
 */
export interface RepoOAuthParams {
  /** Connector type to authenticate with */
  connectorType: RepoConnectorType;
  /** Automation project ID (scopes the connection environment) */
  automationProjectId: string;
  /** Optional custom connection name (auto-generated if omitted) */
  connectionName?: string;
}

/**
 * Result of a successful repo OAuth connection.
 */
export interface RepoOAuthResult {
  /** The created connection resource */
  connection: ConnectionResource;
  /** Connection name or ARM resource ID to store in sandbox config */
  connectionId: string;
  /** Connector type (azuredevops / github) */
  connectorType: RepoConnectorType;
  /** Automation project ID */
  automationProjectId: string;
  /** Test result (if tested) */
  testResult: TestConnectionResult | null;
}

/**
 * OAuth flow state (same as useOAuthConnection for consistency).
 */
export type RepoOAuthFlowState =
  | 'idle'
  | 'creating'
  | 'authenticating'
  | 'confirming'
  | 'syncing'
  | 'testing'
  | 'success'
  | 'error';

/**
 * Return type for useRepoOAuthConnection hook.
 */
export interface UseRepoOAuthConnectionReturn {
  /** Current state of the OAuth flow */
  state: RepoOAuthFlowState;
  /** Error message if flow failed */
  error: string | null;
  /** Successful result (connection + metadata) */
  result: RepoOAuthResult | null;
  /** Initiate OAuth connection flow */
  connect: (params: RepoOAuthParams) => Promise<RepoOAuthResult | null>;
  /** Cancel any ongoing flow */
  cancel: () => void;
  /** Reset state to idle */
  reset: () => void;
  /** Whether a flow is in progress */
  isInProgress: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const POLLING_TIMEOUT_MS = 5 * 60 * 1000;
const POLLING_INTERVAL_MS = 500;
// After the user completes consent, the connection's token is bound
// server-side by the consent service but can take a few seconds to propagate
// onto the connection resource. Source-control connections have no /sync
// endpoint to force-finalize this, so we poll the connection status until it
// reports authenticated (or we hit the timeout) instead of reporting failure
// on the first — often premature — check.
const CONNECTION_STATUS_POLL_INTERVAL_MS = 2000;
const CONNECTION_STATUS_POLL_TIMEOUT_MS = 30 * 1000;
// Grace period after popup.closed is detected before treating as cancelled.
// COOP (Cross-Origin-Opener-Policy) on login.microsoftonline.com can sever
// the popup reference, making popup.closed return true while the popup is
// still open. We keep listening on BroadcastChannel during this period.
const POPUP_CLOSED_GRACE_MS = 3 * 60 * 1000; // 3 minutes

interface OAuthCallbackResult {
  code: string | null;
  error?: string;
  errorDescription?: string;
  cancelled?: boolean;
  timedOut?: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

export function useRepoOAuthConnection(): UseRepoOAuthConnectionReturn {
  const [state, setState] = useState<RepoOAuthFlowState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RepoOAuthResult | null>(null);

  const { account, getOttoToken } = useAuth();

  // Build context independently — the sandbox creation dialog may not
  // have a selectedApp, so we can't rely on useArmContextBuilder.
  // Prefer route-derived project (useCurrentProject) over the store value,
  // which may be bridged asynchronously on /projects/:projectName pages.
  const { projectName: routeProjectName } = useCurrentProject();
  const storeProjectName = useAppStore((s) => s.selectedProject ?? '');
  const projectName = routeProjectName || storeProjectName;
  const selectedLocation = useAppStore((s) => s.selectedLocation);
  const location = selectedLocation ?? 'eastus';

  const isMounted = useRef(true);
  const pollIntervalRef = useRef<number | null>(null);
  const isProcessingRef = useRef(false);
  const pollStartTimeRef = useRef<number>(0);
  const messageHandlerRef = useRef<((event: MessageEvent) => void) | null>(null);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const popupClosedTimerRef = useRef<number | null>(null);

  // Abort infrastructure: refs for the active popup, cleanup callback, and promise rejection.
  const activePopupRef = useRef<Window | null>(null);
  const activeCleanupRef = useRef<(() => void) | null>(null);

  // Incrementing run ID: cancel()/reset() bump this so connect() can detect
  // a stale run after each await and bail without overwriting state.
  const runIdRef = useRef(0);

  const clearPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    if (popupClosedTimerRef.current !== null) {
      clearTimeout(popupClosedTimerRef.current);
      popupClosedTimerRef.current = null;
    }
    isProcessingRef.current = false;
  }, []);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearPolling();
      if (messageHandlerRef.current) {
        window.removeEventListener('message', messageHandlerRef.current);
        messageHandlerRef.current = null;
      }
      if (broadcastChannelRef.current) {
        try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
        broadcastChannelRef.current = null;
      }
    };
  }, [clearPolling]);

  /**
   * Wait for OAuth callback from the popup window.
   * Uses the same dual-detection strategy as useOAuthConnection:
   * postMessage listener + URL polling.
   */
  const waitForOAuthCode = useCallback(
    (popup: Window): Promise<OAuthCallbackResult> => {
      pollStartTimeRef.current = Date.now();
      clearPolling();

      return new Promise<OAuthCallbackResult>((resolve) => {
        const cleanup = () => {
          if (messageHandlerRef.current) {
            window.removeEventListener('message', messageHandlerRef.current);
          }
          messageHandlerRef.current = null;
          if (broadcastChannelRef.current) {
            try { broadcastChannelRef.current.close(); } catch { /* ignore */ }
            broadcastChannelRef.current = null;
          }
          clearPolling();
          try { popup.close(); } catch { /* COOP may have severed the reference */ }
          activePopupRef.current = null;
          activeCleanupRef.current = null;
        };

        // Expose cleanup so cancel()/reset() can tear down the in-flight flow.
        activePopupRef.current = popup;
        activeCleanupRef.current = () => {
          cleanup();
          resolve({ code: null, cancelled: true });
        };

        const resolveWithCleanup = (result: OAuthCallbackResult) => {
          cleanup();
          if (result.code !== null && result.code !== '') {
            isProcessingRef.current = true;
          }
          resolve(result);
        };

        // postMessage listener (primary — more reliable)
        const messageHandler = (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          if (!event.data || typeof event.data !== 'object') return;

          // Only handle messages from our OAuthCallbackPage (identified by
          // the 'oauth-callback' type sentinel) or legacy messages with
          // explicit code/error fields.
          const { type, code, error: oauthError, error_description: oauthErrorDescription } = event.data;
          const isOAuthMessage = type === 'oauth-callback' || code !== undefined || oauthError !== undefined;
          if (!isOAuthMessage) return;

          if (code !== undefined && typeof code !== 'string') return;
          if (oauthError !== undefined && typeof oauthError !== 'string') return;

          if (oauthError) {
            resolveWithCleanup({
              code: null,
              error: oauthError,
              ...(typeof oauthErrorDescription === 'string' && { errorDescription: oauthErrorDescription }),
            });
          } else {
            // code may be a string (explicit consent code) or undefined
            // (Azure handled auth internally — empty code signals success).
            resolveWithCleanup({ code: code ?? '' });
          }
        };
        window.addEventListener('message', messageHandler);
        messageHandlerRef.current = messageHandler;

        // Also listen via BroadcastChannel (fallback for when COOP blocks window.opener)
        if ('BroadcastChannel' in window) {
          try {
            const channel = new BroadcastChannel(OAUTH_CHANNEL_NAME);
            channel.onmessage = (event: MessageEvent) => {
              const data = event.data;
              if (!data || typeof data !== 'object') return;
              const { type, code, error: bcError, error_description: bcErrorDescription } = data;
              const isOAuthMessage = type === 'oauth-callback' || code !== undefined || bcError !== undefined;
              if (!isOAuthMessage) return;
              if (code !== undefined && typeof code !== 'string') return;
              if (bcError !== undefined && typeof bcError !== 'string') return;

              if (bcError) {
                resolveWithCleanup({
                  code: null,
                  error: bcError,
                  ...(typeof bcErrorDescription === 'string' && { errorDescription: bcErrorDescription }),
                });
              } else {
                resolveWithCleanup({ code: code ?? '' });
              }
            };
            broadcastChannelRef.current = channel;
          } catch {
            // BroadcastChannel not supported
          }
        }

        // URL polling (fallback)
        pollIntervalRef.current = window.setInterval(() => {
          const elapsed = Date.now() - pollStartTimeRef.current;

          if (elapsed > POLLING_TIMEOUT_MS) {
            resolveWithCleanup({ code: null, timedOut: true });
            return;
          }

          try {
            if (popup.closed) {
              // The popup reference reports closed. This can mean:
              // 1. COOP (Cross-Origin-Opener-Policy) on the login page severed
              //    the popup reference — the popup is still open.
              // 2. The user actually closed the popup (before or after auth).
              //
              // In case 1, BroadcastChannel will deliver the callback when auth
              // completes. In case 2, no callback will arrive.
              //
              // Strategy: Stop polling the popup (reference is useless). Keep
              // BroadcastChannel/postMessage listeners active. Set a grace timer
              // so we don't wait the full 5 minutes if the user truly cancelled.
              if (pollIntervalRef.current !== null) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              // Guard against duplicate timer if interval fired twice
              if (popupClosedTimerRef.current === null) {
                const remaining = Math.max(0, POLLING_TIMEOUT_MS - elapsed);
                const graceMs = Math.min(remaining, POPUP_CLOSED_GRACE_MS);
                popupClosedTimerRef.current = window.setTimeout(() => {
                  resolveWithCleanup({ code: null, cancelled: true });
                }, graceMs);
              }
              return;
            }
          } catch {
            // COOP may block access
          }

          if (isProcessingRef.current) return;

          try {
            const popupUrl = popup.location.href;
            if (popupUrl.includes(window.location.origin)) {
              const urlParams = new URLSearchParams(new URL(popupUrl).search);
              const code = urlParams.get('code');
              const urlError = urlParams.get('error');
              const urlErrorDescription = urlParams.get('error_description');

              if (code) {
                resolveWithCleanup({ code });
                return;
              }
              if (urlError) {
                resolveWithCleanup({
                  code: null,
                  error: urlError,
                  ...(urlErrorDescription && { errorDescription: urlErrorDescription }),
                });
                return;
              }
              if (popupUrl.includes('/oauth/callback')) {
                // The popup landed on the callback page without code or error.
                // Azure/ADO sometimes handles auth internally (no explicit code).
                // Treat this as success with empty code — confirmRepoConsent
                // is skipped when code is empty.
                resolveWithCleanup({ code: '' });
                return;
              }
            }
          } catch {
            // Cross-origin error expected while on OAuth page
          }
        }, POLLING_INTERVAL_MS);
      });
    },
    [clearPolling],
  );

  /**
   * Initiate the full OAuth flow for a repository connection.
   *
   * IMPORTANT: The popup window must be opened synchronously in the user's
   * click handler to avoid browser popup blockers. We open a blank popup
   * first, then navigate it to the consent URL after async setup completes.
   */
  const connect = useCallback(
    async (params: RepoOAuthParams): Promise<RepoOAuthResult | null> => {
      // Capture run ID so we can detect cancellation after each await.
      const myRunId = ++runIdRef.current;
      const isCancelled = () => runIdRef.current !== myRunId || !isMounted.current;

      setError(null);
      setResult(null);

      // Open popup immediately (synchronous with user click) to avoid
      // popup blockers. We'll navigate it to the consent URL later.
      const popup = window.open(
        'about:blank',
        'repo-oauth-popup',
        'width=600,height=700,scrollbars=yes',
      );

      if (!popup) {
        setError('Failed to open popup. Please allow popups for this site.');
        setState('error');
        return null;
      }

      try {
        // ARM mock mode: use synthetic values for localhost control plane.
        // Must check before the projectName guard so local dev works
        // without a selected project.
        const ctx: AppContext = isArmMockMode()
          ? { projectName: 'mock-project', appName: '', getToken: async () => 'mock-token' }
          : (() => {
              if (!projectName) {
                throw new Error('Unable to build context — ensure you are signed in and have selected a project.');
              }
              return { getToken: getOttoToken, projectName, appName: '' };
            })();

        if (!account) {
          popup.close();
          setError('No authenticated user found');
          setState('error');
          return null;
        }

        if (!params.automationProjectId?.trim()) {
          popup.close();
          setError('No project selected — select a project before connecting.');
          setState('error');
          return null;
        }

        const connector: RepoConnectorConfig = getRepoConnector(params.connectorType);
        const connectionName =
          params.connectionName ?? generateRepoConnectionName(params.connectorType);

        // Step 1: Create connection resource
        setState('creating');
        telemetryService.trackEvent('repo.oauth.started', {
          connectorType: params.connectorType,
          automationProjectId: params.automationProjectId,
        });

        const createdConnection = await createRepoConnection({
          ctx,
          connector,
          automationProjectId: params.automationProjectId,
          connectionName,
        }, location);

        if (isCancelled()) { popup.close(); return null; }

        // Step 2: Get consent link
        setState('authenticating');
        const redirectUrl = buildOAuthRedirectUrl();

        const { consentLink } = await getRepoConsentLink({
          ctx,
          connectionName,
          redirectUrl,
          objectId: account.localAccountId ?? account.homeAccountId ?? 'local-user',
          tenantId: account.tenantId ?? 'local-tenant',
        }, location);

        // Step 3: Navigate the already-open popup to the consent URL
        popup.location.href = consentLink;

        // Step 4: Wait for callback
        const oauthCallback = await waitForOAuthCode(popup);

        if (isCancelled()) return null;

        if (oauthCallback.error) {
          const errorCode = extractOAuthErrorCode(oauthCallback.error);
          setError(getOAuthErrorMessage(errorCode, oauthCallback.errorDescription));
          setState('error');
          return null;
        }

        if (oauthCallback.timedOut) {
          setError('Authentication timed out');
          setState('error');
          return null;
        }

        if (oauthCallback.cancelled) {
          setError('Authentication was cancelled');
          setState('error');
          return null;
        }

        const oauthCode = oauthCallback.code;
        if (oauthCode === null) {
          setError('Authentication was cancelled or timed out');
          setState('error');
          return null;
        }

        // Step 5: Confirm consent code (if we received one)
        if (oauthCode) {
          setState('confirming');

          await confirmRepoConsent({
            ctx,
            connectionName,
            code: oauthCode,
            objectId: account.localAccountId ?? account.homeAccountId ?? 'local-user',
            tenantId: account.tenantId ?? 'local-tenant',
          }, location);

          if (isCancelled()) return null;
        }

        // No sync step is performed in this hook: source-control connections
        // have no /sync endpoint (unlike managed-connector routes), so we
        // cannot force-finalize the token. The consent service binds the token
        // server-side once the user completes the popup flow.

        // Step 6: Wait for the connection to finalize authentication.
        //
        // The token binding can take a few seconds to propagate onto the
        // connection resource after the callback returns. Poll the connection
        // status until it reports authenticated rather than reporting failure
        // on the first (often premature) check. This is the source-control
        // equivalent of the sync + retry step in the managed-connector flow.
        setState('testing');
        let testResultData = await testRepoConnection(ctx, connectionName, location);

        const statusPollDeadline = Date.now() + CONNECTION_STATUS_POLL_TIMEOUT_MS;
        while (!testResultData.success && Date.now() < statusPollDeadline) {
          await new Promise((r) => setTimeout(r, CONNECTION_STATUS_POLL_INTERVAL_MS));
          if (isCancelled()) return null;
          testResultData = await testRepoConnection(ctx, connectionName, location);
        }

        if (isCancelled()) return null;

        // Build result
        // Backend expects just the connection name (not the full resource path).
        // It resolves the name across all apps in the project at execution time.
        const oauthResult: RepoOAuthResult = {
          connection: createdConnection,
          connectionId: connectionName,
          connectorType: params.connectorType,
          automationProjectId: params.automationProjectId,
          testResult: testResultData,
        };

        if (!testResultData.success) {
          // Connection created but test failed — still return result
          // so the user can see the connection and retry later
          setError(testResultData.message ?? 'Connection test failed');
          setState('error');
          setResult(oauthResult);
          telemetryService.trackEvent('repo.oauth.test_failed', {
            connectorType: params.connectorType,
          });
          return oauthResult;
        }

        // Success
        setState('success');
        setResult(oauthResult);
        telemetryService.trackEvent('repo.oauth.completed', {
          connectorType: params.connectorType,
          automationProjectId: params.automationProjectId,
        });

        return oauthResult;
      } catch (err) {
        if (isCancelled()) return null;
        popup.close();

        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setState('error');
        telemetryService.trackEvent('repo.oauth.failed', {
          connectorType: params.connectorType,
          error: errorMessage,
        });
        return null;
      }
    },
    [account, getOttoToken, projectName, location, waitForOAuthCode],
  );

  /**
   * Abort the in-flight OAuth flow: close the popup, remove the
   * message listener, clear polling, and resolve the pending promise
   * so `connect()` does not hang.
   */
  const abortActiveFlow = useCallback(() => {
    clearPolling();
    if (activeCleanupRef.current) {
      activeCleanupRef.current();
      activeCleanupRef.current = null;
    } else {
      // No in-flight waitForOAuthCode — close the popup if it's still open.
      if (activePopupRef.current && !activePopupRef.current.closed) {
        activePopupRef.current.close();
      }
      activePopupRef.current = null;
    }
    if (messageHandlerRef.current) {
      window.removeEventListener('message', messageHandlerRef.current);
      messageHandlerRef.current = null;
    }
  }, [clearPolling]);

  const cancel = useCallback(() => {
    runIdRef.current++;
    abortActiveFlow();
    setState('idle');
    setError('Flow cancelled');
  }, [abortActiveFlow]);

  const reset = useCallback(() => {
    runIdRef.current++;
    abortActiveFlow();
    setState('idle');
    setError(null);
    setResult(null);
  }, [abortActiveFlow]);

  return {
    state,
    error,
    result,
    connect,
    cancel,
    reset,
    isInProgress: state !== 'idle' && state !== 'success' && state !== 'error',
  };
}

// =============================================================================
// UTILITY
// =============================================================================

/**
 * Get descriptive text for repo OAuth flow state.
 */
export function getRepoOAuthStateMessage(state: RepoOAuthFlowState): string {
  switch (state) {
    case 'idle':
      return 'Ready to connect';
    case 'creating':
      return 'Setting up connection...';
    case 'authenticating':
      return 'Waiting for authorization...';
    case 'confirming':
      return 'Confirming authorization...';
    case 'syncing':
      return 'Syncing connection...';
    case 'testing':
      return 'Testing connection...';
    case 'success':
      return 'Connected successfully!';
    case 'error':
      return 'Connection failed';
    default:
      return '';
  }
}
